import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Typography,
  Paper,
  CircularProgress,
} from '@mui/material';
import ObserverApi, {
  IngestionSourceDocType,
  ObserverIngestionPlatform,
  RagObserverResult,
} from '../apis/observerApi';
import LayeredAutocompleteSelect from '../../components/LayeredAutocompleteSelect';
import IngestionRunDetails, {
  formatIngestionStatusLabel,
  IngestionRunDetailsPayload,
  readIngestionDetails,
} from '../../components/IngestionRunDetails';
import { KB_RAG_SOURCE_DOC_TYPES } from '../../utils/constants';

/**
 * RAG Observer: internal tabs — Query | KB index cleanup | Ticket index cleanup | Ingest.
 * Design: MUI Tabs (query form + results table) vs stacked cleanup panels (observer API).
 *
 * Query tab — optional client filter uses one text field but meaning depends on Source:
 * - kb_rag: Horizon `customers.id` (KB Vertex namespace).
 * - ticket_rag: PSA company/customer id (aligned with `past_tickets.client_id` / ticket ingest).
 */

interface MSP {
  id: number;
  name: string;
}

interface RagObserverPanelProps {
  msps: MSP[];
}

type RagPanelTab = 'query' | 'kbCleanup' | 'ticketCleanup' | 'ingest';

const unwrapGrpcValue = (value: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (typeof value === 'object' && value !== null && 'value' in (value as Record<string, unknown>)) {
    const inner = (value as { value?: unknown }).value;
    return inner == null ? '' : String(inner);
  }
  return String(value);
};

const normalizeRagResult = (result: RagObserverResult): RagObserverResult => ({
  ...result,
  source: (unwrapGrpcValue(result.source) as 'kb_rag' | 'ticket_rag') || 'kb_rag',
  title: unwrapGrpcValue(result.title),
  content: unwrapGrpcValue(result.content),
  url: unwrapGrpcValue(result.url),
  source_doc_type: unwrapGrpcValue(result.source_doc_type),
  source_doc_id: unwrapGrpcValue(result.source_doc_id),
  ticket_id: unwrapGrpcValue(result.ticket_id),
  created_at: unwrapGrpcValue(result.created_at),
  distance: unwrapGrpcValue(result.distance),
  workflow_id: unwrapGrpcValue(result.workflow_id),
});

const RagObserverPanel: React.FC<RagObserverPanelProps> = ({ msps }) => {
  const api = useMemo(() => new ObserverApi(), []);
  const [panelTab, setPanelTab] = useState<RagPanelTab>('query');
  const [source, setSource] = useState<'kb_rag' | 'ticket_rag'>('kb_rag');
  const [query, setQuery] = useState('');
  const [mspId, setMspId] = useState('');
  /** Scoped client id: Horizon customer id for kb_rag queries; PSA company id for ticket_rag. */
  const [clientId, setClientId] = useState('');
  /** KB queries only: Vertex `source_doc_type` filter (required when Source is kb_rag). */
  const [docType, setDocType] = useState<string>('kb');
  const [ticketStatusesInput, setTicketStatusesInput] = useState('');
  /** ticket_rag: Vertex/metadata priority restrict (optional); normalized lowercase server-side */
  const [ticketPriority, setTicketPriority] = useState('');
  const [excludeTicketId, setExcludeTicketId] = useState('');
  /** ticket_rag: Vertex ticket created_at lower bound (ISO date or datetime, UTC) */
  const [ticketCreatedAfter, setTicketCreatedAfter] = useState('');
  /** ticket_rag: Vertex ticket created_at upper bound (ISO date or datetime, UTC) */
  const [ticketCreatedBefore, setTicketCreatedBefore] = useState('');
  /** ticket_rag: empty = search both PROBLEM_SUMMARY and RESOLUTION_SUMMARY facets */
  const [ticketEmbeddingKind, setTicketEmbeddingKind] = useState('');
  const [topK, setTopK] = useState(5);
  const [results, setResults] = useState<RagObserverResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [cleanupMspScope, setCleanupMspScope] = useState('');
  const [cleanupClientId, setCleanupClientId] = useState('');
  const [cleanupDocTypes, setCleanupDocTypes] = useState<string[]>([]);
  const [cleanupSourceDocId, setCleanupSourceDocId] = useState('');
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupError, setCleanupError] = useState<string | null>(null);
  const [cleanupResult, setCleanupResult] = useState<string | null>(null);

  const [ticketCleanupMspScope, setTicketCleanupMspScope] = useState('');
  const [ticketCleanupClientId, setTicketCleanupClientId] = useState('');
  const [ticketCleanupTicketId, setTicketCleanupTicketId] = useState('');
  const [ticketCleanupType, setTicketCleanupType] = useState('');
  const [ticketCleanupLoading, setTicketCleanupLoading] = useState(false);
  const [ticketCleanupError, setTicketCleanupError] = useState<string | null>(null);
  const [ticketCleanupResult, setTicketCleanupResult] = useState<string | null>(null);
  const [ingestRunning, setIngestRunning] = useState(false);
  const [ingestStatus, setIngestStatus] = useState<string>('');
  const [ingestDetails, setIngestDetails] = useState<IngestionRunDetailsPayload | null>(null);
  const [ingestCompletedAt, setIngestCompletedAt] = useState<string | null>(null);
  const [ingestError, setIngestError] = useState<string | null>(null);
  const [ingestLoading, setIngestLoading] = useState(false);
  const [ingestMspId, setIngestMspId] = useState('');
  const [ingestSourceDocType, setIngestSourceDocType] = useState<IngestionSourceDocType>('guide');
  const [ingestPlatformId, setIngestPlatformId] = useState('');
  const [ingestPlatforms, setIngestPlatforms] = useState<ObserverIngestionPlatform[]>([]);

  const mspOptions = useMemo(
    () => msps.map((msp) => ({ value: String(msp.id), label: msp.name })),
    [msps],
  );

  const cleanupScopeOptions = useMemo(
    () => [
      { value: '', label: 'Select cleanup scope…' },
      { value: 'all', label: 'All MSPs' },
      ...msps.map((msp) => ({
        value: String(msp.id),
        label: `${msp.name} (${msp.id})`,
      })),
    ],
    [msps],
  );

  /** Same control as MSP field: LayeredAutocompleteSelect avoids Select portal click/z-index issues in this layout. */
  const sourceOptions = useMemo(
    () => [
      { value: 'kb_rag', label: 'kb_rag' },
      { value: 'ticket_rag', label: 'ticket_rag' },
    ],
    [],
  );

  const kbDocTypeOptions = useMemo(() => KB_RAG_SOURCE_DOC_TYPES, []);

  const kbCleanupDocTypeOptions = useMemo(() => KB_RAG_SOURCE_DOC_TYPES, []);

  const ingestSourceDocTypeOptions = useMemo(
    () => [
      { value: 'guide', label: 'guide' },
      { value: 'kb', label: 'kb' },
      { value: 'hudu_articles', label: 'hudu_articles' },
      { value: 'itglue_docs', label: 'itglue_docs' },
      { value: 'psa_tickets', label: 'psa_tickets' },
    ],
    [],
  );

  const ingestPlatformOptions = useMemo(
    () =>
      ingestPlatforms.map((platform) => ({
        value: String(platform.id),
        label: `${platform.name} (${platform.id})`,
      })),
    [ingestPlatforms],
  );
  const ingestNeedsPlatform = ingestSourceDocType === 'psa_tickets';

  const kbDocTypeValues = useMemo(() => new Set(KB_RAG_SOURCE_DOC_TYPES.map((o) => o.value)), []);

  const ticketEmbeddingKindOptions = useMemo(
    () => [
      { value: '', label: 'Both (PROBLEM_SUMMARY + RESOLUTION_SUMMARY)' },
      { value: 'PROBLEM_SUMMARY', label: 'PROBLEM_SUMMARY' },
      { value: 'RESOLUTION_SUMMARY', label: 'RESOLUTION_SUMMARY' },
    ],
    [],
  );

  /** Gate Run Query: MSP + non-empty query; KB source requires a known doc type from the dropdown. */
  const canRunQuery = useMemo(() => {
    if (!query.trim() || !mspId) return false;
    if (source === 'kb_rag') return kbDocTypeValues.has(docType);
    return true;
  }, [query, mspId, source, docType, kbDocTypeValues]);

  const ragQueryClientField = useMemo(() => {
    if (source === 'ticket_rag') {
      return {
        label: 'PSA company ID (optional)',
        helperText:
          'Matches past_tickets.client_id / Vertex ticket clientId. Not Horizon customers.id.',
      } as const;
    }
    return {
      label: 'Horizon client ID (optional)',
      helperText: 'KB scope: Horizon customers.id on kb_* vectors.',
    } as const;
  }, [source]);

  const handleQuery = async () => {
    if (!canRunQuery) {
      setError(
        !mspId
          ? 'MSP is required.'
          : !query.trim()
            ? 'Query is required.'
            : 'Doc type is required for KB queries.',
      );
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await api.queryRag({
        query: query.trim(),
        source,
        mspId: Number(mspId),
        clientId: clientId || undefined,
        docType: source === 'kb_rag' ? docType : undefined,
        ticketStatuses:
          source === 'ticket_rag' && ticketStatusesInput.trim() ? ticketStatusesInput.trim() : undefined,
        ticketPriority:
          source === 'ticket_rag' && ticketPriority.trim() ? ticketPriority.trim() : undefined,
        excludeTicketId: source === 'ticket_rag' ? excludeTicketId || undefined : undefined,
        ticketCreatedAfter:
          source === 'ticket_rag' && ticketCreatedAfter.trim() ? ticketCreatedAfter.trim() : undefined,
        ticketCreatedBefore:
          source === 'ticket_rag' && ticketCreatedBefore.trim()
            ? ticketCreatedBefore.trim()
            : undefined,
        topK: topK || undefined,
        ticketEmbeddingKind:
          source === 'ticket_rag' && ticketEmbeddingKind.trim()
            ? ticketEmbeddingKind.trim()
            : undefined,
      });
      setResults((response.results || []).map(normalizeRagResult));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to run RAG query');
    } finally {
      setLoading(false);
    }
  };

  const runCleanup = async (execute: boolean) => {
    if (!cleanupMspScope) {
      setCleanupError('Choose a cleanup scope (one MSP or All MSPs).');
      return;
    }
    setCleanupLoading(true);
    setCleanupError(null);
    setCleanupResult(null);
    try {
      const docTypesToRun: Array<string | undefined> =
        cleanupDocTypes.length > 0 ? cleanupDocTypes : [undefined];
      let executeMode = execute;
      let matchedCount = 0;
      let vertexDeleteOk = 0;
      let vertexDeleteFailed = 0;
      let mysqlDeleted = 0;
      const kbDocumentIds = new Set<number>();
      const errors: string[] = [];
      const perTypeSummary: string[] = [];

      for (const sourceDocType of docTypesToRun) {
        const res = await api.cleanupKbRag({
          ...(cleanupMspScope !== 'all'
            ? { mspId: Number(cleanupMspScope) }
            : {}),
          execute,
          clientId: cleanupClientId.trim() || undefined,
          sourceDocType: sourceDocType || undefined,
          sourceDocId: cleanupSourceDocId.trim() || undefined,
        });
        executeMode = Boolean(res.executeMode);
        matchedCount += res.matchedCount || 0;
        vertexDeleteOk += res.vertexDeleteOk || 0;
        vertexDeleteFailed += res.vertexDeleteFailed || 0;
        mysqlDeleted += res.mysqlDeleted || 0;
        for (const id of res.kbDocumentIds || []) kbDocumentIds.add(id);
        for (const err of res.errors || []) errors.push(err);
        perTypeSummary.push(
          `${sourceDocType || 'any'} => matched:${res.matchedCount}, deleted:${res.mysqlDeleted}, vertex_ok:${res.vertexDeleteOk}, vertex_failed:${res.vertexDeleteFailed}`,
        );
      }

      const summary = [
        `scope: ${cleanupMspScope === 'all' ? 'all_msps' : `msp_${cleanupMspScope}`}`,
        `mode: ${executeMode ? 'execute' : 'dry-run'}`,
        `source_doc_types: ${cleanupDocTypes.length ? cleanupDocTypes.join(', ') : 'any'}`,
        `matched: ${matchedCount}`,
        `vertex_ok: ${vertexDeleteOk}`,
        `vertex_failed: ${vertexDeleteFailed}`,
        `mysql_deleted: ${mysqlDeleted}`,
        `sample_ids: ${Array.from(kbDocumentIds).slice(0, 20).join(', ')}`,
        `per_type: ${perTypeSummary.join(' | ')}`,
        errors.length ? `errors: ${errors.join('; ')}` : '',
      ]
        .filter(Boolean)
        .join('\n');
      setCleanupResult(summary);
    } catch (err) {
      setCleanupError(err instanceof Error ? err.message : 'Cleanup request failed');
    } finally {
      setCleanupLoading(false);
    }
  };

  const handleCleanupPreview = () => {
    void runCleanup(false);
  };

  const handleCleanupExecute = () => {
    const scopeLabel =
      cleanupMspScope === 'all'
        ? 'ALL MSPs (every matching kb_documents row system-wide)'
        : `MSP ID ${cleanupMspScope}`;
    const msg =
      cleanupMspScope === 'all'
        ? `DESTRUCTIVE: Execute KB cleanup for ${scopeLabel}? Only optional cleanup filters below apply. This cannot be undone.`
        : `Execute KB cleanup for ${scopeLabel}? Matching kb_documents rows and Vertex kb_* vectors will be removed. This cannot be undone.`;
    if (!window.confirm(msg)) return;
    void runCleanup(true);
  };

  const runTicketCleanup = async (execute: boolean) => {
    if (!ticketCleanupMspScope) {
      setTicketCleanupError('Choose a cleanup scope (one MSP or All MSPs).');
      return;
    }
    setTicketCleanupLoading(true);
    setTicketCleanupError(null);
    setTicketCleanupResult(null);
    try {
      const res = await api.cleanupTicketRag({
        ...(ticketCleanupMspScope !== 'all'
          ? { mspId: Number(ticketCleanupMspScope) }
          : {}),
        execute,
        clientId: ticketCleanupClientId.trim() || undefined,
        ticketId: ticketCleanupTicketId.trim() || undefined,
        ticketType: ticketCleanupType.trim() || undefined,
      });
      const summary = [
        `scope: ${ticketCleanupMspScope === 'all' ? 'all_msps' : `msp_${ticketCleanupMspScope}`}`,
        `mode: ${res.executeMode ? 'execute' : 'dry-run'}`,
        `matched: ${res.matchedCount}`,
        `vertex_ok: ${res.vertexDeleteOk}`,
        `vertex_failed: ${res.vertexDeleteFailed}`,
        `mysql_deleted: ${res.mysqlDeleted}`,
        `sample_past_ticket_ids: ${(res.pastTicketIds || []).slice(0, 20).join(', ')}`,
        (res.errors || []).length ? `errors: ${(res.errors || []).join('; ')}` : '',
      ]
        .filter(Boolean)
        .join('\n');
      setTicketCleanupResult(summary);
    } catch (err) {
      setTicketCleanupError(
        err instanceof Error ? err.message : 'Ticket cleanup request failed',
      );
    } finally {
      setTicketCleanupLoading(false);
    }
  };

  const handleTicketCleanupPreview = () => {
    void runTicketCleanup(false);
  };

  const handleTicketCleanupExecute = () => {
    const scopeLabel =
      ticketCleanupMspScope === 'all'
        ? 'ALL MSPs (every matching past_tickets row system-wide)'
        : `MSP ID ${ticketCleanupMspScope}`;
    const msg =
      ticketCleanupMspScope === 'all'
        ? `DESTRUCTIVE: Execute ticket RAG cleanup for ${scopeLabel}? Only optional filters below apply. This cannot be undone.`
        : `Execute ticket RAG cleanup for ${scopeLabel}? Matching past_tickets rows and Vertex ticket_* vectors will be removed. This cannot be undone.`;
    if (!window.confirm(msg)) return;
    void runTicketCleanup(true);
  };

  const loadIngestStatus = useCallback(async () => {
    if (!ingestMspId) {
      setIngestRunning(false);
      setIngestStatus('');
      setIngestDetails(null);
      setIngestCompletedAt(null);
      return;
    }
    if (ingestNeedsPlatform && !ingestPlatformId) {
      setIngestRunning(false);
      setIngestStatus('Select a PSA platform to view ticket ingestion status.');
      setIngestDetails(null);
      setIngestCompletedAt(null);
      return;
    }
    try {
      const status = await api.getIngestionStatus(
        Number(ingestMspId),
        ingestSourceDocType,
        ingestNeedsPlatform && ingestPlatformId ? Number(ingestPlatformId) : undefined,
      );
      const isRunning = Boolean(status?.isRunning);
      setIngestRunning(isRunning);
      setIngestStatus(status?.ingestion?.status || 'Idle');
      setIngestDetails(readIngestionDetails(status?.ingestion as { details?: IngestionRunDetailsPayload; details_json?: unknown } | null));
      setIngestCompletedAt(status?.ingestion?.completed_at || null);
    } catch (err) {
      setIngestError(err instanceof Error ? err.message : 'Failed to load ingest status');
    }
  }, [api, ingestMspId, ingestSourceDocType, ingestNeedsPlatform, ingestPlatformId]);

  useEffect(() => {
    const loadPlatforms = async () => {
      if (!ingestMspId || !ingestNeedsPlatform) {
        setIngestPlatforms([]);
        setIngestPlatformId('');
        return;
      }
      try {
        const platforms = await api.getIngestionPlatforms(Number(ingestMspId), 'psa');
        setIngestPlatforms(platforms);
        if (!platforms.some((platform) => String(platform.id) === ingestPlatformId)) {
          setIngestPlatformId(platforms[0] ? String(platforms[0].id) : '');
        }
      } catch (err) {
        setIngestError(err instanceof Error ? err.message : 'Failed to load ingest platforms');
        setIngestPlatforms([]);
        setIngestPlatformId('');
      }
    };
    void loadPlatforms();
  }, [api, ingestMspId, ingestNeedsPlatform, ingestPlatformId]);

  const triggerIngest = async () => {
    if (!ingestMspId) {
      setIngestError('MSP is required.');
      return;
    }
    if (ingestNeedsPlatform && !ingestPlatformId) {
      setIngestError('PSA platform is required for psa_tickets ingestion.');
      return;
    }
    setIngestLoading(true);
    setIngestError(null);
    try {
      const resp = await api.triggerIngestion(
        Number(ingestMspId),
        ingestSourceDocType,
        ingestNeedsPlatform && ingestPlatformId ? Number(ingestPlatformId) : undefined,
      );
      if (resp?.alreadyRunning) {
        setIngestStatus(resp?.ingestion?.status || 'RUNNING');
        setIngestCompletedAt(resp?.ingestion?.completed_at || null);
        setIngestRunning(true);
      } else {
        setIngestStatus(resp?.ingestion?.status || 'RUNNING 0%');
        setIngestCompletedAt(resp?.ingestion?.completed_at || null);
        setIngestRunning(true);
      }
    } catch (err) {
      setIngestError(err instanceof Error ? err.message : 'Failed to trigger ingest');
    } finally {
      setIngestLoading(false);
      void loadIngestStatus();
    }
  };

  useEffect(() => {
    if (panelTab !== 'ingest') return;
    void loadIngestStatus();
    const interval = setInterval(() => {
      void loadIngestStatus();
    }, 10000);
    return () => clearInterval(interval);
  }, [panelTab, loadIngestStatus]);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          RAG Observer
        </Typography>
        <Tabs
          value={panelTab}
          onChange={(_, v: RagPanelTab) => setPanelTab(v)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Query" value="query" />
          <Tab label="KB index cleanup" value="kbCleanup" />
          <Tab label="Ticket index cleanup" value="ticketCleanup" />
          <Tab label="Ingest" value="ingest" />
        </Tabs>

        {panelTab === 'query' && (
          <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Run direct embedding + metadata filtered queries against KB and ticket vector indexes.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={3}>
            <LayeredAutocompleteSelect
              label="Source"
              value={source}
              onChange={(v) =>
                setSource(v === 'ticket_rag' ? 'ticket_rag' : 'kb_rag')
              }
              options={sourceOptions}
              disableClearable
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <LayeredAutocompleteSelect
              label="MSP *"
              value={mspId}
              onChange={setMspId}
              options={mspOptions}
              noOptionsText="No MSPs available"
              disableClearable
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Query"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label={ragQueryClientField.label}
              helperText={ragQueryClientField.helperText}
              value={clientId}
              onChange={(event) => setClientId(event.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Top K"
              type="number"
              inputProps={{ min: 1, max: 20 }}
              value={topK}
              onChange={(event) => setTopK(Number(event.target.value || 5))}
              fullWidth
            />
          </Grid>
          {source === 'kb_rag' && (
            <Grid item xs={12} md={3}>
              <LayeredAutocompleteSelect
                label="Doc type *"
                value={docType}
                onChange={setDocType}
                options={kbDocTypeOptions}
                disableClearable
                fullWidth
              />
            </Grid>
          )}
          {source === 'ticket_rag' && (
            <>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Ticket Statuses (optional)"
                  helperText="Comma-separated; matches normalized status names in the ticket index"
                  value={ticketStatusesInput}
                  onChange={(event) => setTicketStatusesInput(event.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Ticket Priority (optional)"
                  helperText="e.g. low, high — normalized lowercase"
                  value={ticketPriority}
                  onChange={(event) => setTicketPriority(event.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Exclude Ticket ID (optional)"
                  value={excludeTicketId}
                  onChange={(event) => setExcludeTicketId(event.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Created after (optional)"
                  helperText="ISO date or datetime (UTC), e.g. 2025-01-01"
                  value={ticketCreatedAfter}
                  onChange={(event) => setTicketCreatedAfter(event.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Created before (optional)"
                  helperText="ISO date or datetime (UTC). Inclusive calendar-day upper bound."
                  value={ticketCreatedBefore}
                  onChange={(event) => setTicketCreatedBefore(event.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <LayeredAutocompleteSelect
                  label="Embedding category (ticket_rag)"
                  value={ticketEmbeddingKind}
                  onChange={setTicketEmbeddingKind}
                  options={ticketEmbeddingKindOptions}
                  fullWidth
                />
              </Grid>
            </>
          )}
          <Grid item xs={12} md={3}>
            <Button
              type="button"
              variant="contained"
              onClick={handleQuery}
              disabled={loading || !canRunQuery}
              fullWidth
              sx={{ height: '56px' }}
            >
              {loading ? <CircularProgress size={20} /> : 'Run Query'}
            </Button>
          </Grid>
        </Grid>

        {results.length === 0 ? (
          <Alert severity="info">No results yet. Submit a query to inspect RAG retrieval output.</Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Source</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Distance</TableCell>
                  <TableCell>Metadata</TableCell>
                  <TableCell>Content</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((result, index) => (
                  <TableRow key={`${result.source}-${result.title}-${index}`}>
                    <TableCell>{result.source}</TableCell>
                    <TableCell>{result.title || 'N/A'}</TableCell>
                    <TableCell>{result.distance || 'N/A'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {result.source_doc_type && <Typography variant="caption">docType: {result.source_doc_type}</Typography>}
                        {result.source_doc_id && <Typography variant="caption">docId: {result.source_doc_id}</Typography>}
                        {result.ticket_id && <Typography variant="caption">ticketId: {result.ticket_id}</Typography>}
                        {result.created_at && <Typography variant="caption">createdAt: {result.created_at}</Typography>}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {result.content}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
          </>
        )}

        {panelTab === 'kbCleanup' && (
          <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          <strong>Docs / KB index.</strong> Choose cleanup scope (one MSP or <strong>All MSPs</strong>), then optional{' '}
          <code>client_id</code> / <code>source_doc_type</code> / <code>source_doc_id</code>. Preview = dry-run; Execute removes Vertex{' '}
          <code>kb_&lt;id&gt;</code> then MySQL <code>kb_documents</code> rows.
        </Typography>
        {cleanupError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {cleanupError}
          </Alert>
        )}
        {cleanupResult && (
          <Alert severity={cleanupResult.includes('errors:') ? 'warning' : 'success'} sx={{ mb: 2 }}>
            <Typography component="pre" variant="body2" sx={{ whiteSpace: 'pre-wrap', m: 0 }}>
              {cleanupResult}
            </Typography>
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={3}>
            <LayeredAutocompleteSelect
              label="Cleanup scope"
              value={cleanupMspScope}
              onChange={setCleanupMspScope}
              options={cleanupScopeOptions}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Cleanup: client_id (optional)"
              helperText="kb_documents.client_id"
              value={cleanupClientId}
              onChange={(e) => setCleanupClientId(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              multiple
              options={kbCleanupDocTypeOptions}
              value={kbCleanupDocTypeOptions.filter((option) =>
                cleanupDocTypes.includes(option.value),
              )}
              onChange={(_, selected) =>
                setCleanupDocTypes(selected.map((option) => option.value))
              }
              disableCloseOnSelect
              getOptionLabel={(option) => option.label}
              getOptionKey={(option) => option.value}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cleanup: source_doc_type (optional)"
                  helperText="Multi-select; empty means any type"
                />
              )}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Cleanup: source_doc_id (optional)"
              helperText="Hudu article or asset id (exact)"
              value={cleanupSourceDocId}
              onChange={(e) => setCleanupSourceDocId(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={handleCleanupPreview}
                  disabled={cleanupLoading || !cleanupMspScope}
                >
                  {cleanupLoading ? <CircularProgress size={20} /> : 'Preview cleanup'}
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  color="warning"
                  onClick={handleCleanupExecute}
                  disabled={cleanupLoading || !cleanupMspScope}
                >
                  Execute cleanup
                </Button>
              </Box>
              {!cleanupMspScope && (
                <Typography variant="caption" color="text.secondary">
                  Choose cleanup scope to enable Preview / Execute.
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
          </>
        )}

        {panelTab === 'ticketCleanup' && (
          <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          <strong>Tickets / past_tickets index.</strong> Same shared vector index as KB — datapoints{' '}
          <code>ticket_&lt;past_tickets.id&gt;</code>. Scope: one MSP or <strong>All MSPs</strong>; optional{' '}
          PSA <code>client_id</code> (company id) / PSA <code>ticket_id</code> / <code>ticket_type</code>.
          Preview = dry-run only.
        </Typography>
        {ticketCleanupError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {ticketCleanupError}
          </Alert>
        )}
        {ticketCleanupResult && (
          <Alert
            severity={ticketCleanupResult.includes('errors:') ? 'warning' : 'success'}
            sx={{ mb: 2 }}
          >
            <Typography component="pre" variant="body2" sx={{ whiteSpace: 'pre-wrap', m: 0 }}>
              {ticketCleanupResult}
            </Typography>
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={3}>
            <LayeredAutocompleteSelect
              label="Ticket cleanup scope"
              value={ticketCleanupMspScope}
              onChange={setTicketCleanupMspScope}
              options={cleanupScopeOptions}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Cleanup: PSA company id (optional)"
              helperText="past_tickets.client_id (PSA company, not Horizon)"
              value={ticketCleanupClientId}
              onChange={(e) => setTicketCleanupClientId(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Cleanup: ticket_id (optional)"
              helperText="PSA ticket id (exact)"
              value={ticketCleanupTicketId}
              onChange={(e) => setTicketCleanupTicketId(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Cleanup: ticket_type (optional)"
              value={ticketCleanupType}
              onChange={(e) => setTicketCleanupType(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={handleTicketCleanupPreview}
                  disabled={ticketCleanupLoading || !ticketCleanupMspScope}
                >
                  {ticketCleanupLoading ? <CircularProgress size={20} /> : 'Preview ticket cleanup'}
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  color="warning"
                  onClick={handleTicketCleanupExecute}
                  disabled={ticketCleanupLoading || !ticketCleanupMspScope}
                >
                  Execute ticket cleanup
                </Button>
              </Box>
              {!ticketCleanupMspScope && (
                <Typography variant="caption" color="text.secondary">
                  Choose ticket cleanup scope to enable Preview / Execute.
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
          </>
        )}
        {panelTab === 'ingest' && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Trigger ingestion for one MSP and one source_doc_type.
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={3}>
                <LayeredAutocompleteSelect
                  label="MSP *"
                  value={ingestMspId}
                  onChange={setIngestMspId}
                  options={mspOptions}
                  noOptionsText="No MSPs available"
                  disableClearable
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <LayeredAutocompleteSelect
                  label="Source doc type"
                  value={ingestSourceDocType}
                  onChange={(v) => setIngestSourceDocType(v as IngestionSourceDocType)}
                  options={ingestSourceDocTypeOptions}
                  disableClearable
                  fullWidth
                />
              </Grid>
              {ingestNeedsPlatform && (
                <Grid item xs={12} md={4}>
                  <LayeredAutocompleteSelect
                    label="PSA platform *"
                    value={ingestPlatformId}
                    onChange={setIngestPlatformId}
                    options={ingestPlatformOptions}
                    noOptionsText="No enabled PSA platforms for this MSP"
                    disableClearable
                    fullWidth
                  />
                </Grid>
              )}
            </Grid>
            {ingestError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {ingestError}
              </Alert>
            )}
            <Alert severity={ingestRunning ? 'info' : 'success'} sx={{ mb: 2 }}>
              {ingestStatus ? formatIngestionStatusLabel(ingestStatus) : 'Status unknown'}
              {ingestCompletedAt ? ` (completed: ${ingestCompletedAt})` : ''}
              <IngestionRunDetails status={ingestStatus} details={ingestDetails} />
            </Alert>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                type="button"
                variant="contained"
                onClick={triggerIngest}
                disabled={
                  ingestLoading ||
                  ingestRunning ||
                  !ingestMspId ||
                  (ingestNeedsPlatform && !ingestPlatformId)
                }
              >
                {ingestLoading ? <CircularProgress size={20} /> : ingestRunning ? 'Ingest Running' : 'Trigger Ingest'}
              </Button>
              <Button type="button" variant="outlined" onClick={loadIngestStatus} disabled={ingestLoading}>
                Refresh Status
              </Button>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default RagObserverPanel;
